/**
 * JSON export strategy implementation.
 *
 * Produces pretty-printed JSON array with:
 * - Column keys as property names
 * - Formatted values based on column format types
 * - 2-space indentation for readability
 */

import type { ColumnDefinition, ExportStrategy } from '../types';
import { formatValue } from '../formatters';

/**
 * JSON export strategy.
 *
 * Converts tabular data to pretty-printed JSON format.
 */
export class JsonExportStrategy implements ExportStrategy {
    /**
     * Exports data to a JSON Blob.
     *
     * @param data - Array of data objects to export
     * @param columns - Column definitions for keys and formatting
     * @returns Blob with JSON content (application/json MIME type)
     */
    export(data: unknown[], columns: ColumnDefinition[]): Blob {
        const exportData = data.map((item) => {
            const record = item as Record<string, unknown>;
            const exportRecord: Record<string, string> = {};

            for (const col of columns) {
                const rawValue = record[col.key];
                exportRecord[col.key] = formatValue(rawValue, col.format);
            }

            return exportRecord;
        });

        // Pretty-print with 2-space indentation
        const jsonContent = JSON.stringify(exportData, null, 2);
        return new Blob([jsonContent], { type: 'application/json;charset=utf-8' });
    }
}
